/**
 * Domain types — mirror of the as-life-api shaped responses.
 * Source of truth: KB "Data Types & Enums" + Swagger at /api/docs.
 * Updated 2026-06-23 for the backend "AS Life — Backend Done" contract
 * (waves 1–4): feed pagination, profile fields, learning/calendar/communities/
 * faq/tasks/preferences/search/uploads, EDO time-off + remind, push devices.
 */
import type { Localized } from '../i18n'
import type { Tone } from '../theme/tones'

// ─────────── Enums (string unions) ───────────
// STUDY_LEAVE (учебный отпуск, ТЗ-2026-006) и BUSINESS_TRIP (командировка, ТЗ-2026-003)
// добавлены волной 07.2026 — зеркало серверного EdoType.
// RECRUITMENT (подбор персонала, ТЗ-2026-011) — портальный процесс без 1С (волна 07.2026).
// SICK_LEAVE (больничный лист) — волна 07.2026, мягкое предупреждение о пересечении с др. отсутствиями.
// PATERNITY_LEAVE (ТЗ-2026-014) — document.отпуск с vacation_type=paternity_leave, справка обяз.
// MATERNITY_LEAVE (ТЗ-2026-012) — document.БольничныйЛист черновиком (НЕ портальный SICK_LEAVE).
// CHILD_CARE_LEAVE (ТЗ-2026-013) — document.ОтпускПоУходуЗаРебенком + данные ребёнка; DSMF вручную.
// FREEFORM — документ произвольной формы: тема + текст + вложения, маршрут согласования
// (последовательный, поимённо) собирает сам инициатор. Портальный процесс, без 1С.
// CONTRACT (ТЗ-2026-015) — согласование договорных документов: та же схема, что у произвольного
// документа (маршрут собирает инициатор), но форма типизирована, а финал требует скана
// подписанного сторонами экземпляра.
// IT_WORKS (ТЗ-2026-АКТ001) — согласование и принятие выполненных IT-работ (аутсорс): строки модулей
// согласуют свои согласующие, затем финальное утверждение и протокол. Портальный процесс, без 1С.
export type EdoType = 'VACATION' | 'HIRE' | 'TERMINATION' | 'TIME_OFF' | 'STUDY_LEAVE' | 'BUSINESS_TRIP' | 'RECRUITMENT' | 'SICK_LEAVE'
  | 'PATERNITY_LEAVE' | 'MATERNITY_LEAVE' | 'CHILD_CARE_LEAVE' | 'FREEFORM' | 'CONTRACT' | 'IT_WORKS'

// SUP_REVIEW = у руководителя СП, NOT_REVIEWED = таймер согласования истёк (ТЗ-2026-002, отгул).
// AWAITING_ACCRUAL = начисление бухгалтером; TRAVEL_BOOKING/EXPENSE_REVIEW/ORDER_CREATION/
// CFO_APPROVAL — шаги командировки (ТЗ-2026-003).
// Подбор (ТЗ-2026-011): CEO_REVIEW = согласование ГД; IN_PROGRESS = «В работе» (старт поиска);
// SEARCHING = «Поиск ведётся»; CLOSED = «Закрыта (кандидат найден)»; CANCELLED = «Отменена».
// REGISTERED — больничный лист зарегистрирован (терминальный успех, приказ HR создан в 1С).
export type EdoStatus =
  | 'AWAITING_HIRE_POSTING' | 'AWAITING_TERMINATION_POSTING'
  | 'DRAFT' | 'MGR_REVIEW' | 'SUP_REVIEW' | 'HR_REVIEW' | 'SIGNING'
  | 'APPROVED' | 'POSTED' | 'POST_FAILED' | 'REWORK' | 'REJECTED' | 'NOT_REVIEWED'
  | 'AWAITING_ACCRUAL' | 'TRAVEL_BOOKING' | 'EXPENSE_REVIEW' | 'ORDER_CREATION' | 'CFO_APPROVAL'
  | 'CEO_REVIEW' | 'IN_PROGRESS' | 'SEARCHING' | 'CLOSED' | 'CANCELLED' | 'REGISTERED'
  // DSMF_NOTIFYING — «notifying» из ТЗ-2026-013 §2.2: ждём ручного уведомления в фонд.
  | 'DSMF_NOTIFYING'
  // IN_APPROVAL — произвольный документ у очередного согласующего из маршрута инициатора.
  | 'IN_APPROVAL'
  // AWAITING_SIGNED_COPY — договор: маршрут пройден, автор прикладывает подписанный экземпляр и срок.
  | 'AWAITING_SIGNED_COPY'
  // POSTING — договор: автор отправил подписанный экземпляр, идёт проводка в 1С (→ POSTED / POST_FAILED).
  | 'POSTING'
  // EXIT_INTERVIEW — увольнение (ТЗ-2026-008 шаг 7): HR проводит Exit-интервью.
  | 'EXIT_INTERVIEW'

// SUPERVISOR = руководитель СП. ACCOUNTANT = начисление (учебный); TRAVEL/EXPENSE_REVIEW/
// ORDER/CFO/ACCRUAL — шаги командировки (после проводки табеля — approve-only).
// CEO = согласование ГД; SEARCH = старт поиска (ОИ/HR); CLOSE = закрытие с резюме (подбор).
export type EdoStage =
  | 'MANAGER' | 'SUPERVISOR' | 'HR' | 'SIGNATURE' | 'REWORK'
  | 'ACCOUNTANT' | 'TRAVEL' | 'EXPENSE_REVIEW' | 'ORDER' | 'CFO' | 'ACCRUAL'
  | 'CEO' | 'SEARCH' | 'CLOSE'
  // DSMF — уход за ребёнком (ТЗ-2026-013 §4.2): бухгалтер вручную шлёт уведомление в госфонд.
  | 'DSMF'
  // CUSTOM — шаг маршрута произвольного документа: согласующего выбрал инициатор, роли нет.
  | 'CUSTOM'
  // SIGNED_COPY — договор: автор прикладывает скан подписанного сторонами экземпляра и срок действия.
  | 'SIGNED_COPY'
  // EXIT_INTERVIEW — увольнение: результаты интервью (причина/комментарий/анкета или «отказ»).
  | 'EXIT_INTERVIEW'
  // IT_WORKS: IT_MODULES — согласование строк модулей (решения не общей панелью, а построчно:
  // POST /edo/it-works/:id/lines); IT_FINAL — финальное утверждение принятых работ.
  | 'IT_MODULES' | 'IT_FINAL'

export type EdoTaskStatus =
  | 'OPEN' | 'APPROVED' | 'REJECTED' | 'REWORK' | 'RESUBMITTED' | 'CANCELLED' | 'ESCALATED'

export type ReactionType = 'LIKE' | 'DISLIKE'
export type FeedChannel = 'general' | 'it' | 'hr' | 'pr' | 'ideas'

// Realm roles from the Keycloak token. Mirror of as-life-api APP_ROLES (src/auth/roles.ts) —
// все четыре, включая `gd` (Генеральный директор): realm его выдаёт и бэк гейтит по нему
// эндпоинты, так что он реально приезжает в `Profile.roles`. Без него тип врал: в массиве
// лежало значение вне union'а, а проверка `hasAnyRole(me, ['gd'])` не компилировалась.
// ЭДО-согласующие (hr/gd/…) — это ДРУГОЕ: они резолвятся по справочнику должностей на бэке.
export type AppRole = 'admin' | 'hr' | 'gd' | 'employee'

export type VacationKind = 'basic' | 'unpaid' | 'study'
export type EmploymentType = 'main' | 'internal' | 'external'
export type TerminationBasis = 'agreement' | 'own' | 'employer' | 'term_end'
export type EdoDecision = 'approve' | 'reject' | 'rework' | 'sign' | 'resubmit' | 'cancel'

// ─────────── Users ───────────
export interface UserPosition {
  title: string
  isPrimary: boolean
}

/** Readable office/location attached to a profile (wave 2.2). */
export interface Office {
  id: string
  name: string
  city: string
}

/** Derived activity status (wave 2.2). */
export type Presence = 'online' | 'away' | 'offline'

/** Readable projection status; technical references remain on the server. */
export interface EmploymentRequisite {
  state: 'present' | 'missing' | 'unsupported' | 'invalid' | 'conflict' | 'reference_unresolved' | 'unavailable' | 'stale'
  label: string | null
  code?: string | null
  syncedAt?: string | null
}

export interface Employment {
  id: string
  companyId: string
  companyName: string
  employmentType: string | null
  employmentTypeLabel?: string | null
  employmentTypeState?: EmploymentRequisite['state']
  departmentType?: EmploymentRequisite
  positionSchedule?: EmploymentRequisite
  status: string
  isPrimary: boolean
  position: string | null
  department: string | null
  effectiveDate: string | null
}
export interface EmploymentSummary {
  employments: Employment[]
  employmentSelectionRequired: boolean
}
export type RequestEmployment = Pick<Employment, 'id' | 'companyId' | 'companyName' | 'position'>

export interface Profile {
  employments?: Employment[]
  employmentSelectionRequired?: boolean
  id: string
  email: string
  fullName: string
  jobTitle?: string | null
  department?: string | null
  avatarUrl?: string | null
  phone?: string | null
  grade?: number | null
  managerId?: string | null
  positions?: UserPosition[]
  /** Кто замещает сотрудника прямо сейчас (отпуск в силе); null — никто. */
  deputy?: { id: string; fullName: string } | null
  // wave 2.2 — additional profile fields
  location?: Office | null
  hiredAt?: string | null // ISO — "в компании с"
  birthday?: string | null // ISO — пока null, ждёт источник 1С
  skills?: string[]
  presence?: Presence
  onVacation?: boolean // «в отпуске сегодня» — дериватив бэка из ЭДО (на проде с 2026-07-21, a3f532c)
  vacationUntil?: string | null // ISO-дата окончания отпуска — для тултипа «в отпуске до …»; null, если не в отпуске
  // Живое кадровое назначение из 1С (зеркало zup_org_assignment). Дополняет jobTitle/department,
  // не подменяет. null, если сотрудника нет в зеркале (несматченный табельный / не в 1С).
  zupAssignment?: {
    positionId: string | null
    position: string | null // должность
    unitId: string | null
    unit: string | null // подразделение
    org: string | null
    effectiveDate: string | null // ISO
    syncedAt: string // ISO
  } | null
  // Realm-роли из токена (admin/hr/gd/employee). Гейтят клиентские действия (модерация ленты/новостей, HR-карточки).
  // Возвращается из GET /users/me (roles: user.roles), live с 2026-07. undefined (до загрузки) ⇒ роли нет ⇒ employee.
  roles?: AppRole[]
}

/** Editable profile fields — PATCH /users/me (должность/отдел из 1С, не редактируются). */
export interface ProfilePatch {
  phone?: string
  skills?: string[]
}

// ─────────── HR-редактирование карточек (спека «Редактирование сотрудников (HR)», 2026-07-01) ───────────

/** GET /locations — справочник офисов (активные; пока 2 тестовые из-за отсутствия 1С). */
export interface LocationItem {
  id: string
  name: string
  city: string | null
}

/** Элемент `positions` в PATCH /users/:id — replace-all, titles уникальны, ровно один isPrimary. */
export interface UserPositionInput {
  title: string // обязателен, ≤ 200
  department?: string | null // ≤ 200
  isPrimary?: boolean // primary → становится jobTitle
}

/**
 * PATCH /users/:id (роль hr/admin) — все поля опциональны (patch-семантика):
 * отсутствие поля = «не трогать», `null` = очистить. Строгий whitelist —
 * лишнее поле ⇒ 400. Identity (email/username/роли/аватар) правится не здесь.
 */
export interface UserAdminPatch {
  expectedPrimaryEmploymentId?: string | null
  fullName?: string // ≤ 200
  firstName?: string | null // ≤ 100
  lastName?: string | null // ≤ 100
  middleName?: string | null // ≤ 100
  jobTitle?: string | null // ≤ 200
  department?: string | null // ≤ 200
  grade?: number | null // целое 0…100
  managerId?: string | null // null — снять руководителя (корень оргструктуры)
  locationId?: string | null // id из GET /locations
  hiredAt?: string | null // YYYY-MM-DD
  birthday?: string | null // YYYY-MM-DD
  isActive?: boolean
  showInOrgTree?: boolean // false — скрыть из орг-дерева (остаётся в списке людей)
  positions?: UserPositionInput[] // ≤ 20, replace-all
  phone?: string | null // ≤ 40
  skills?: string[] // ≤ 30, each ≤ 40
}

/** GET /users/:id/full (роль hr/admin) — полная карточка для префилла формы правки. */
export interface DirectoryUserFull {
  employments?: Employment[]
  employmentSelectionRequired?: boolean
  id: string
  email: string
  fullName: string
  firstName: string | null
  lastName: string | null
  middleName: string | null
  jobTitle: string | null
  department: string | null
  grade: number | null
  avatarUrl: string | null
  managerId: string | null
  manager: { id: string; fullName: string } | null
  locationId: string | null
  location: { id: string; name: string; city: string | null } | null
  hiredAt: string | null // ISO
  birthday: string | null // ISO (дата)
  isActive: boolean
  phone: string | null
  skills: string[]
  positions: { title: string; department: string | null; isPrimary: boolean }[]
  orgManuallyEdited: boolean // true → запись курируется вручную, импорт 1С её не трогает
  showInOrgTree: boolean // false → скрыт из орг-дерева (но виден в списке людей и поиске)
}

/** GET /users/departments. */
export interface Department {
  id: string
  name: string
  count: number
}

/** ИИ-ассистент — контракт as-life-api/docs/ai-assistant.md (раздел 2, пользовательские эндпоинты). */
export interface AssistantAccess {
  enabled: boolean
  reason?: 'disabled' | 'no_access'
  allowWrite?: boolean
  limit?: number
  used?: number
}
export type AssistantItem =
  | { role: 'user' | 'assistant'; text: string }
  | { kind: 'tool'; name: string; status: string }
  | { kind: 'confirm'; toolUseId: string; title: string; preview: string; status: 'pending' | 'done' | 'cancelled' }
export interface AssistantConversationSummary {
  id: string
  title: string
  updatedAt: string
}
export interface AssistantConversation {
  id: string
  title: string
  items: AssistantItem[]
}
export interface AssistantStepResponse {
  conversation: AssistantConversation
  pendingToolUses: { id: string; name: string; kind: 'read' | 'write' }[]
  done: boolean
}
export type AssistantStepBody =
  | { action: 'message'; text: string }
  | { action: 'run_tool'; toolUseId: string }
  | { action: 'confirm_tool'; toolUseId: string; approve: boolean }
  | { action: 'continue' }

/** GET /users/absences — «Состояние»: кто отсутствует на дату и почему (согласованные заявки ЭДО). */
export type AbsenceKind = 'vacation' | 'trip' | 'sick' | 'time_off'
export interface Absence {
  id: string // EdoRequest.id
  kind: AbsenceKind
  user: { id: string; fullName: string; jobTitle: string | null; avatarUrl: string | null }
  company: { id: string; name: string } | null
  /** Период: даты YYYY-MM-DD (to=null — без даты окончания, напр. уход за ребёнком), у отгула — ISO со временем. */
  period: { from: string; to: string | null }
  deputy: { id: string; fullName: string } | null // замещающий из заявки на отпуск
}
export interface AbsencesResponse {
  date: string // YYYY-MM-DD, на которую посчитано
  items: Absence[]
}

/** GET /users/departments/tree — вкладка «Подразделения»: юрлицо → подразделение 1С → люди. */
export interface DepartmentTreeMember {
  id: string
  fullName: string
  jobTitle: string | null // должность по назначению 1С
  avatarUrl: string | null
  isHead: boolean // позиция ШР помечена в 1С руководящей
}
export interface DepartmentTreeUnit {
  id: string // Ref_Key подразделения 1С
  name: string
  head: { id: string; fullName: string } | null // null — руководящей позиции в подразделении нет
  members: DepartmentTreeMember[] // руководитель первым, дальше по алфавиту
}
export interface DepartmentTreeCompany {
  id: string
  name: string
  isHolding: boolean
  departments: DepartmentTreeUnit[]
}

/** One row of GET /users/me/balances (источник — 1С; availableDays пока null). */
export interface Balance {
  kind: string // напр. "vacation"
  availableDays: number | null
  totalDays: number | null
  unit: string // напр. "days"
  source: string
  asOf: string | null // ISO
}

export interface BalancesResponse {
  items: Balance[]
}

export interface OrgNode extends Profile {
  reports: OrgNode[]
}

/** Держатель узла оргчарта по должностям; null у узла = вакансия. */
export interface PositionHolder {
  id: string
  fullName: string
  avatarUrl?: string | null
  presence?: Profile['presence']
}

/** Узел оргчарта ПО ДОЛЖНОСТЯМ (`/users/org-chart/positions`): совместитель = несколько узлов. */
export interface PositionNode {
  id: string
  /** 'company' — юрлицо в оргчарте холдинга (holder=null, не вакансия). */
  kind?: 'company'
  /** У узла компании: корп. центр холдинга — во главе дочерних юрлиц. */
  isHolding?: boolean
  title: string
  holder: PositionHolder | null
  reports: PositionNode[]
}

// ─────────── Feed ───────────
export interface Author {
  id: string
  fullName: string
  jobTitle?: string | null
  avatarUrl?: string | null
}

/** Upload descriptor / post attachment (wave 3.2 + 2.3). */
export interface Attachment {
  id: string
  url: string
  mime: string
  size: number
  name: string
}

export interface Post {
  id: string
  title: string
  body: string
  channel: FeedChannel | string
  isPinned: boolean
  author: Author
  createdAt: string
  commentsCount: number
  likes: number
  dislikes: number
  // wave 2.3
  myReaction: ReactionType | null
  updatedAt?: string
  attachments?: Attachment[] | null
}

export interface Comment {
  id: string
  body: string
  postId?: string
  author: Author
  createdAt: string
}

export interface PostDetail extends Post {
  comments: Comment[]
}

export interface ReactionCounts {
  likes: number
  dislikes: number
}

/** Cursor page for GET /feed/posts (wave 2.3 / 3.1). */
export interface PostPage {
  items: Post[]
  nextCursor: string | null
}

/** Body for POST/PATCH /feed/posts. Attach files via attachmentIds (POST /uploads first). */
export interface PostInput {
  title: string
  body: string
  channel?: string
  isPinned?: boolean
  attachmentIds?: string[]
}

// ─────────── EDO ───────────
export interface VacationPayload {
  employmentId?: string
  /** Подача «от имени» (роль proxy): id сотрудника, за которого подаётся заявка. */
  onBehalfOfUserId?: string
  kind: VacationKind
  startDate: string
  endDate: string
  comment?: string | null
  /** Замещающий на время отпуска; обязательность проверяет сервер (руководитель / участник согласований). */
  deputyId?: string | null
}

/**
 * GET /edo/vacation/balance — остаток отпускных дней из 1С (ТЗ-2026-004 §3.2, ФТ-01/КП-01).
 * balanceDays — сумма по всем правам (графа остатка); null, если 1С недоступен.
 */
export interface VacationBalance {
  employeeId: string
  asOfDate: string
  balanceDays: number | null
  byRight: { rightType: string; balanceDays: number }[]
  entitlements: { rightType: string; daysPerYear: number }[]
  source: 'zup' | 'unavailable'
}

export interface HirePayload {
  employmentId?: string
  firstName: string
  lastName: string
  middleName?: string | null
  fullName: string
  orderDate: string
  department: string
  position: string
  salary: number
  schedule: string
  employmentType: EmploymentType
  comment?: string | null
}

export interface TerminationPayload {
  terminationContractVersion?: '2026-10-05'
  terminationReasonId?: string
  terminationReasonName?: string
  reason?: string
  employeeEmploymentId?: string
  employmentId?: string
  employeeId: string
  employeeName?: string
  employeeJobTitle?: string | null
  terminationDate?: string
  /** Legacy requests only; the new draft uses the 1C reference above. */
  basis?: TerminationBasis
  compensateVacation?: boolean
  comment?: string | null
}

/**
 * EdoRequest.payload for type=TIME_OFF (ТЗ-2026-002). Stored server-side after
 * createTimeOff derives durationHours and snapshots the monthly balance.
 */
export interface TimeOffPayload {
  employeeExternalId: string
  timeFrom: string // ISO 8601 UTC (with Z)
  timeTo: string // ISO 8601 UTC (with Z)
  durationHours: number
  absenceReason: string
  comment: string | null
  managerOnLeave: boolean // currently always false
  escalationTriggered: boolean // true once a 3h timer fired and the route escalated
  balanceSnapshot: { period: string; remainingBefore: number }
}

/** Request body for POST /edo/time-off — initiator + durationHours are derived server-side. */
export interface TimeOffInput {
  employmentId?: string
  /** Подача «от имени» (роль proxy): id сотрудника, за которого подаётся заявка. */
  onBehalfOfUserId?: string
  timeFrom: string // ISO 8601 UTC (with Z)
  timeTo: string // ISO 8601 UTC (with Z)
  absenceReason: string // must be one of GET /edo/time-off/reasons
  comment?: string // ≤ 500 chars
}

/** GET /edo/time-off/balance — current user's monthly time-off limit. */
export interface TimeOffBalance {
  employeeId: string
  period: string // "2026-07"
  limitHoursTotal: number
  limitHoursUsed: number
  limitHoursRemaining: number
}

/** Request body for POST /edo/sick-leave — рук → HR (приказ), REGISTERED терминальный. */
export type SickLeaveInput = {
  employmentId?: string
  /** Подача «от имени» (роль proxy): id сотрудника, за которого подаётся заявка. */
  onBehalfOfUserId?: string
  dateFrom: string
  dateTo: string
  comment?: string
}

/**
 * POST /edo/paternity-leave (ТЗ-2026-014). uploadId — справка из клиники, ОБЯЗАТЕЛЬНА (§2.3):
 * файл сперва грузится как POST /uploads?purpose=edo_doc, сюда идёт его id. Длительность считает
 * сервер по диапазону дат.
 */
export type PaternityLeaveInput = {
  employmentId?: string
  /** Подача «от имени» (роль proxy): id сотрудника, за которого подаётся заявка. */
  onBehalfOfUserId?: string
  dateFrom: string
  dateTo: string
  uploadId: string
  comment?: string
}

/**
 * POST /edo/maternity-leave (ТЗ-2026-012). Мед. основание (номер листка, медорганизация) вносит HR
 * вручную из EMAS — портал его НЕ передаёт.
 */
export type MaternityLeaveInput = {
  employmentId?: string
  /** Подача «от имени» (роль proxy): id сотрудника, за которого подаётся заявка. */
  onBehalfOfUserId?: string
  dateFrom: string
  dateTo: string
  comment?: string
}

/** Данные ребёнка для ТЧ ДанныеОДетях в 1С (ТЗ-2026-013 §3.1). */
export type ChildInput = {
  lastName: string
  firstName: string
  middleName?: string | null
  birthDate: string
  birthOrder?: number
  gender?: string | null
}

/**
 * POST /edo/child-care-leave (ТЗ-2026-013). dateTo — плановая (дата рождения + 3 года) ИЛИ
 * отсутствует: бессрочный уход (§4.1), тогда в 1С уходит null.
 */
export type ChildCareLeaveInput = {
  employmentId?: string
  /** Подача «от имени» (роль proxy): id сотрудника, за которого подаётся заявка. */
  onBehalfOfUserId?: string
  dateFrom: string
  dateTo?: string
  child: ChildInput
  comment?: string
}

/** Пересечение больничного с другим отсутствием (мягкое предупреждение из ответа createSickLeave). */
export type AbsenceOverlap = {
  id: string
  type: EdoType
  number: number
  from: string
  to: string
}

/**
 * Ставка суточных из справочника (GET /edo/business-trip/per-diem-rates). Ориентировочная —
 * для ОЦЕНКИ расходов; точный расчёт ведёт 1С. countryCode='DEFAULT' — фолбэк.
 */
/**
 * GET /edo/business-trip/destinations — направления из зеркала норм 1С для пикера.
 * stranaId (GUID страны) уходит в заявке как destinationCountryCode; города несут суточные из 1С.
 * Заграничный регистр — внутренних (AZ) направлений тут нет.
 */
export interface TripDestination {
  stranaId: string
  country: string
  cities: { city: string; perDiem: number; currency: string }[]
}

/** Оценка командировочных для показа согласующему/CEO (снапшот в payload; в 1С не уходит). */
/** Строка разбивки суточных по сотруднику (ТЗ-2026-003 v1.2 §4.2). */
export interface PerDiemLine {
  employeeId: string
  name: string
  dailyAmount: number
  days: number
  amount: number
}

/**
 * Суточные с разбивкой по участникам. Норма — зеркально из регистра 1С по стране+городу
 * (решение 16.07.2026), `amount` — ИТОГО = сумма строк. В 1С не уходит (1С считает сам).
 */
export interface PerDiemEstimate {
  dailyAmount: number
  currency: string
  days: number
  travellers: number
  amount: number
  perEmployee: PerDiemLine[]
  rateSource: 'zup_trip_norm'
  note: string
}

/**
 * EdoRequest.payload for type=BUSINESS_TRIP (ТЗ-2026-003 v2.0). Точный расчёт (суточные/средний)
 * считает 1С — портал сумму не шлёт. `estimate` — ориентировочная сумма для показа CEO при
 * утверждении расходов (не уходит в 1С). employeeNames — снапшот для отображения.
 */
export interface BusinessTripPayload {
  employeeIds: string[]
  employeeExternalIds: string[]
  employeeNames: string[]
  destinationCountryCode: string // strana_id (GUID) из пикера или ISO alpha-2/3 (легаси)
  destinationCityName: string
  /** Полный адрес прибытия (v1.2 Блок 2) — уточнение + данные для трансфера. В 1С не уходит. */
  destinationAddress: string | null
  purpose: string
  dateFrom: string // YYYY-MM-DD
  dateTo: string // YYYY-MM-DD
  dateOfReturnToWork: string // YYYY-MM-DD (требование контракта 1С)
  durationDays: number
  estimate?: PerDiemEstimate | null
  comment: string | null
}

/** Request body for POST /edo/business-trip. employeeIds пуст/не задан → едет сам инициатор. */
export interface BusinessTripInput {
  employeeEmploymentIds?: string[]
  employmentId?: string
  employeeIds?: string[]
  destinationCountryCode: string // strana_id (GUID) из пикера или ISO alpha-2/3
  destinationCityName: string // ≤ 200
  destinationAddress?: string // ≤ 500 — полный адрес прибытия (трансфер), необязателен
  purpose: string // ≤ 1000
  dateFrom: string // YYYY-MM-DD
  dateTo: string // YYYY-MM-DD
  dateOfReturnToWork: string // YYYY-MM-DD, ≥ dateTo
  comment?: string // ≤ 1000
}

/** Грейд вакансии из справочника (GET /edo/recruitment/grades). triggersCeo → шаг ГД в маршруте. */
export interface RecruitmentGrade {
  code: string
  nameRu: string
  nameEn: string
  examples?: string | null
  triggersCeo: boolean
}

/** Тело POST /edo/recruitment (подбор персонала, ТЗ-2026-011). Полная анкета §6.2 (7 секций). */
export interface RecruitmentInput {
  employmentId?: string
  // Секция 1: информация о вакансии
  jobTitle: string // ≤ 120
  jobGradeCode: string // код из RecruitmentGrade
  department: string // ≤ 200
  managerName?: string
  project?: string
  workLocation?: string
  employmentRate?: 'full' | 'part'
  workSchedule?: '5d' | '6d' | '2shift' | '3shift'
  note?: string
  // Секция 2: обоснование и бюджет
  vacancyType?: 'new' | 'budgeted'
  staffUnitType?: 'intern' | 'temporary' | 'project'
  termOfValidity?: string
  reasons?: string[]
  replacesName?: string
  salaryFrom?: number
  salaryTo?: number
  currency?: 'AZN' | 'USD' | 'EUR'
  plannedStartDate?: string // YYYY-MM-DD
  otherReasons?: string
  // Секция 3: требования к кандидату
  ageLimit?: string
  experience?: string
  education?: string
  certificates?: string
  languages?: { name: string; level?: string }[]
  techSkills?: string
  personalQualities?: string
  // Секция 4: доп. условия
  travelDomestic?: boolean
  travelInternational?: boolean
  ownCar?: boolean
  driverLicense?: boolean
  driverCategory?: string
  otherConditions?: string
  // Секция 5: проверяемые навыки
  testingRequired?: boolean
  testTypes?: string[]
  otherSkills?: string
  // Секция 6: участники собеседований
  interviewers?: { fullName: string; position?: string }[]
  // Общее
  reason?: string
  comment?: string // ≤ 1000
}

/** Строка дашборда подбора (GET /edo/recruitment/dashboard). */
export interface RecruitmentDashboardItem {
  employment?: RequestEmployment | null
  id: string
  /** Номер документа — та же сквозная последовательность, что и у остальных процессов. */
  number: number
  jobTitle: string | null
  department: string | null
  jobGradeCode: string | null
  jobGradeNameRu: string | null
  status: EdoStatus
  stage: EdoStage | null
  assignee: string | null
  createdAt: string
  statusSince: string
  daysInStatus: number
}
export interface RecruitmentDashboard {
  items: RecruitmentDashboardItem[]
  stats: { active: number; closed: number; cancelled: number; rejected: number; avgDaysToClose: number | null }
}

/** Тело POST /edo/recruitment/:id/close. resumeUploadId ОБЯЗАТЕЛЕН (КП-03), прочее — опц. */
export interface CloseRecruitmentInput {
  resumeUploadId: string
  candidateFullName?: string
  candidatePhone?: string
  candidateEmail?: string
  candidatePosition?: string
  agreedSalary?: number
  candidateStartDate?: string
  source?: string
  note?: string
}

/**
 * payload заявки на подбор = вся анкета RecruitmentInput (бэк спредит DTO как есть,
 * edo.service.ts createRecruitment) + серверные производные. Поля, которые бэк пишет
 * через `?? null`, и старые строки БД могут содержать null — у них тип расширен.
 */
export interface RecruitmentPayload
  extends Omit<RecruitmentInput, 'reason' | 'comment' | 'plannedStartDate' | 'salaryFrom' | 'salaryTo' | 'currency'> {
  /** Нормализованный jobTitle — ключ дедупа (ФТ-14), на UI не выводится. */
  titleNorm?: string
  jobGradeNameRu?: string
  triggersCeo?: boolean
  reason?: string | null
  plannedStartDate?: string | null
  salaryFrom?: number | null
  salaryTo?: number | null
  currency?: string | null
  comment?: string | null
  /** Снапшот данных кандидата — появляется после закрытия (POST /edo/recruitment/:id/close). */
  candidate?: {
    fullName?: string | null
    phone?: string | null
    email?: string | null
    position?: string | null
    agreedSalary?: number | null
    startDate?: string | null
    source?: string | null
    note?: string | null
    closedById?: string | null
  } | null
}

/**
 * POST /edo/freeform — документ произвольной формы. approvers — id согласующих; parallel
 * задаёт режим: true — все получают документ разом и решают независимо, false/пусто —
 * последовательно, в порядке списка. Менять маршрут и режим после подачи нельзя.
 * uploadIds — файлы, ранее загруженные как POST /uploads?purpose=edo_doc.
 */
export interface FreeformInput {
  employmentId?: string
  title: string
  body: string
  approvers: string[]
  parallel?: boolean
  uploadIds?: string[]
}

// ─────────── IT-работы (ТЗ-2026-АКТ001) ───────────
// Зеркало серверных типов edo/it-works (it-works.helpers.ts, it-works-catalog.service.ts,
// dto/it-works-form.dto.ts): поля один в один, даты в JSON приходят строками.
export type ItWorksDirection = 'GEORGIA' | 'BAKU'
export type ItWorksCurrency = 'GEL' | 'AZN' | 'USD'
/** Грузия: один `summary` + любое число `attachment`. Баку: только `report`. */
export type ItWorksReportKind = 'summary' | 'attachment' | 'report'
export interface ItWorksPerson { id: string; fullName: string }

export interface ItWorksReport {
  id: string
  kind: ItWorksReportKind
  uploadId: string
  /** Автор отчёта (справочник авторов) — он не согласует и не утверждает собственную работу. */
  authorId?: string
  note?: string
}
export interface ItWorksLine {
  id: string
  /** null = «Другое»: тогда обязателен `otherName`. */
  moduleId: string | null
  otherName?: string
  /** Только Баку: отчёт, к которому относится строка. */
  reportId?: string
  approverId: string
  note?: string
}

/**
 * payload поданной заявки (ItWorksPayload бэка). Черновик хранит форму неполной (обязательно лишь
 * `direction`), а после доработки очищенные поля приходят `null` — читать как «нет значения».
 */
export interface ItWorksPayload {
  direction: ItWorksDirection
  contractorId: string
  /** Отчётный месяц `YYYY-MM`. */
  period: string
  /** Необязательные границы периода `YYYY-MM-DD`. */
  periodFrom?: string
  periodTo?: string
  amount: number
  currency: ItWorksCurrency
  invoiceUploadId?: string
  finalApproverId: string
  recipients: string[]
  comment?: string
  reports: ItWorksReport[]
  modules: ItWorksLine[]
  observers?: string[]
}

export interface ItWorksReportInput {
  id?: string
  kind?: ItWorksReportKind
  uploadId?: string
  authorId?: string
  note?: string
}
export interface ItWorksLineInput {
  id?: string
  moduleId?: string | null
  otherName?: string
  reportId?: string
  approverId?: string
  note?: string
}
/**
 * Тело POST /edo/it-works/drafts, /:id/save и patch `payload.itWorks` при переотправке с доработки
 * (ItWorksFormDto). Черновик может быть неполным — обязательно лишь направление; правила заявки
 * проверяет сервер при подаче. Пустой `id` у отчёта/строки — новая запись (id проставит сервер).
 */
export interface ItWorksInput {
  direction: ItWorksDirection
  contractorId?: string
  period?: string
  periodFrom?: string
  periodTo?: string
  amount?: number
  currency?: ItWorksCurrency
  invoiceUploadId?: string
  finalApproverId?: string
  recipients?: string[]
  comment?: string
  reports?: ItWorksReportInput[]
  modules?: ItWorksLineInput[]
}

/** GET /edo/it-works/options?direction= — справочники формы (только активные записи и люди). */
export interface ItWorksOptions {
  direction: ItWorksDirection
  companyId: string
  currency: ItWorksCurrency
  modules: { id: string; name: string; defaultApprover: ItWorksPerson | null }[]
  contractors: { id: string; name: string; taxId: string | null; currency: string }[]
  authors: { id: string; name: string }[]
  finalApprovers: ItWorksPerson[]
  defaultFinalApproverId: string | null
  recipients: ItWorksPerson[]
  defaultContractorId: string | null
}

/** Решение по строке модуля, определившее её статус (LineDecisionLike бэка). */
export interface ItWorksLineDecision {
  lineId: string
  lineLabel: string
  round: number
  decision: 'approve' | 'rework'
  /** Подпись содержимого строки на момент решения: по ней одобрение переезжает в следующий раунд. */
  signature: string
  approverId: string
  /** Кого замещал решивший (заместитель на время отпуска); null — решал сам. */
  actingForId: string | null
  comment: string | null
  decidedAt: string
}
/** Состояние строки модуля в текущем раунде (LineState бэка). */
export interface ItWorksLineState {
  lineId: string
  label?: string
  /** Назначенный согласующий; у исключённой строки — null. */
  approverId: string | null
  status: 'pending' | 'approved' | 'rework' | 'excluded'
  /** Раунд решения, определившего статус; null у pending. */
  decidedRound: number | null
  /** Одобрение прошлого раунда перенесено без изменений. */
  carried: boolean
  decision: ItWorksLineDecision | null
}
/** Тело POST /edo/it-works/:id/lines: 1–50 решений; комментарий обязателен при `rework`. */
export interface ItWorksLineDecisionInput {
  lineId: string
  decision: 'approve' | 'rework'
  comment?: string
}

// ─────────── Договор (ТЗ-2026-015) ───────────
// Значения — зеркало серверных перечислений (create-contract.dto.ts). Подписи к ним живут
// в edo-labels.ts (CONTRACT_ENUM_LABEL), здесь только коды.
export type ContractCompany = 'AS_QRUP' | 'AZMAN' | 'AS_INSHAAT' | 'AS_DEVELOPMENT' | 'OTHER'
export type ContractDocType = 'contract' | 'addendum' | 'annex' | 'other'
/** Источник текста договора: свой шаблон или присланный контрагентом. */
export type ContractSource = 'internal_template' | 'counterparty_template'
/** Типы, дополняющие существующий договор → требуют его номер. */
export const CONTRACT_PARENT_DOC_TYPES: ContractDocType[] = ['addendum', 'annex']
export type ContractServiceKind = 'services' | 'goods' | 'works' | 'rent'
/** Длительность договора — справочно; точные даты собираются в финале. */
export type ContractDuration = '1y' | '2y' | '3y' | '5y' | 'perpetual'
export type ContractCurrency = 'AZN' | 'USD' | 'EUR' | 'RUB' | 'GBP'
export type ContractPaymentType = 'prepaid' | 'on_fact' | 'staged' | 'prepaid_staged'
/** Местный (резидент AZ) или зарубежный контрагент — ставит инициатор, реестр ГНС знает только местных. */
export type ContractCounterpartyKind = 'local' | 'foreign'
/** Юр- или физлицо — у зарубежного контрагента (у местного вид отдаёт реестр e-taxes). */
export type ContractCounterpartyType = 'legalEntity' | 'naturalPerson'
/** Страна из справочника 1С базы компании (`GET /edo/contract/countries`). */
export interface ContractCountry { id: string; name: string }

/**
 * Карточка контрагента из `POST /counterparties/lookup` (ТЗ-2026-015 §7). Заполняется из
 * публичных ручек e-taxes.gov.az; банковских реквизитов там нет вовсе — вводятся руками.
 * `stale` — карточка отдана без сверки с госпорталом (недоступен либо интеграция выключена).
 */
export interface CounterpartyCard {
  voen: string
  type: 'legalEntity' | 'naturalPerson'
  name: string
  legalAddress: string | null
  legitimate: string | null
  vatPayer: boolean | null
  active: boolean | null
  risky: boolean
  source: string
  checkedAt: string | null
  stale: boolean
}

/**
 * Заявка на согласование договора. Обязательные поля — по §4.2 ТЗ (самый строгий из трёх
 * списков). `observers` — «информируемые» (§5.1): получают уведомление и доступ, но задачи
 * не получают и согласование не блокируют. Маршрут строго последовательный — режима «одновременно»
 * у договора нет (прототип 05.08.2026).
 */
export interface ContractInput {
  employmentId?: string
  title: string
  company: ContractCompany
  source: ContractSource
  docType: ContractDocType
  /** Обязательно и только при docType = other. */
  otherType?: string
  /** Регистрационный номер договора — присваивают юристы, при подаче обычно пуст. */
  docNo?: string
  /** Обязательно и только при docType ∈ addendum | annex (§4.3). */
  contractNo?: string
  serviceKind: ContractServiceKind
  /** Только признак договора: не создаёт и не требует отдельного файла. */
  hasAdditionalAppendix: boolean
  duration: ContractDuration
  counterpartyKind: ContractCounterpartyKind
  /** У местного — ВÖİN (10 цифр), у зарубежного — иностранный номер или пусто. */
  counterpartyVoen: string
  /** Необязательно: после проверки по ВÖİN реестр отдаёт наименование сам (§7). */
  counterpartyName?: string
  /**
   * Реквизиты зарубежного контрагента (19.10.2026) — обязательны, если договор компании
   * проводится в 1С: без них 1С не создаст контрагента. Иначе не шлются (бэк отобьёт 400).
   */
  counterpartyType?: ContractCounterpartyType
  /** GUID страны из справочника 1С базы компании. */
  counterpartyCountryId?: string
  counterpartyAddress?: string
  subject: string
  /** Необязательна (правка юристов 18.09.2026): сумма бывает неизвестна на момент подачи. */
  amount?: number
  currency: ContractCurrency
  paymentType: ContractPaymentType
  approvers: string[]
  uploadIds: string[]
  observers?: string[]
  /**
   * Срок действия при подаче НЕ отправляется — его вносит автор на шаге SIGNED_COPY вместе со
   * сканом подписанного экземпляра (решение бизнеса 27.08.2026, перенос на автора — 23.09.2026).
   * В payload заявки поля есть и до этого шага лежат пустыми, поэтому тип их сохраняет.
   */
  dateFrom?: string
  dateTo?: string
  /** Свободное пользовательское описание; технической ставкой 1С не является. */
  vatTerms?: string
  comment?: string
}

/** payload договора (что сохранил бэк): поля формы + снимок маршрута и информируемых. */
export interface ContractPayload extends Omit<ContractInput, 'uploadIds' | 'hasAdditionalAppendix' | 'counterpartyCountryId'> {
  /** Страна зарубежного контрагента, как её сохранил бэк: GUID базы + название из справочника. */
  counterpartyCountry?: ContractCountry | null
  /** У старых заявок признака нет. */
  hasAdditionalAppendix?: boolean
  /** Наследие старой формы; карточка показывает как условия НДС, 1С использует как ставку. */
  vatRate?: string
  parallel: boolean
}

/** payload произвольного документа (что сохранил бэк). approvers+parallel — снимок маршрута. */
export interface FreeformPayload {
  title: string
  body: string
  approvers: string[]
  parallel?: boolean
}

export type EdoPayload =
  | VacationPayload | HirePayload | TerminationPayload | TimeOffPayload | BusinessTripPayload
  | RecruitmentPayload | FreeformPayload | ItWorksPayload

export interface EdoTask {
  id: string
  requestId?: string
  stage: EdoStage
  status: EdoTaskStatus | string
  assigneeId?: string | null
  candidateGroup?: string | null
  decisionById?: string | null
  decisionComment?: string | null
  decidedAt?: string | null
  createdAt?: string
  // Ответственные для маршрута согласования: assignee — текущий адресат;
  // decisionBy — кто фактически принял решение (для показа «на ком шаг» / «кто согласовал»).
  assignee?: { id: string; fullName: string; jobTitle?: string | null } | null
  decisionBy?: { id: string; fullName: string; jobTitle?: string | null } | null
  request?: {
    employment?: RequestEmployment | null
    id: string
    number: number
    /** Номер в формате 1С (ITO-2026-000128) — у IT_WORKS показывается вместо DOC-NNNN. */
    portalRequestId?: string | null
    type: EdoType
    payload: EdoPayload
    initiator?: { fullName: string; externalId?: string | null }
  }
}

export interface EdoResult {
  terminationState?: 'draft' | 'awaiting_posting' | 'posted'
  hireState?: 'awaiting_posting' | 'posted' | 'cancelled'
  zupDocumentId?: string
  employeeId?: string
  physicalPersonId?: string
  employeeCode?: string
  documentNo?: string
  message?: string
  vacationBalanceDays?: number
  compensationDays?: number
  // Начисление отпускных (ТЗ-2026-004 шаг 11): портальный статус выплаты (не 1С).
  paymentStatus?: 'Pending' | 'Paid'
  vacationPay?: number | null
  accruedById?: string
}

export interface EdoRequest {
  employment?: RequestEmployment | null
  id: string
  /** Номер документа — сквозной по всем типам процессов; показывать через docNo() (DOC-0042). */
  number: number
  /** Номер документа в формате 1С (LGL-2026-000099 и т.п.); null у заявок до ввода формата. */
  portalRequestId?: string | null
  type: EdoType
  status: EdoStatus
  initiatorId?: string
  /** В списке «Мои заявки» бэк отдаёт инициатора — для пометки «от имени: ФИО» у заявок proxy. */
  initiator?: { id: string; fullName: string }
  /** Подача «от имени» (роль proxy): кто фактически подал. null/absent — обычная подача. */
  submittedById?: string | null
  submittedBy?: { id: string; fullName: string } | null
  payload: EdoPayload
  isSigned: boolean
  signedBy?: { fullName: string } | null
  result?: EdoResult | null
  processInstanceId?: string | null
  tasks: EdoTask[]
  createdAt: string
  updatedAt?: string
}

/** Роль пользователя в документе на вкладке «С моим участием». */
export type EdoParticipantRole = 'initiator' | 'submitter' | 'approver' | 'signer'

/** GET /edo/participating — заявка + мои роли в ней (инициатор/согласующий/подписант). */
export interface ParticipatingRequest extends EdoRequest {
  myRoles: EdoParticipantRole[]
}

/**
 * One entry in the request-card timeline (GET /edo/requests/:id → auditEvents[]).
 * actor = null → system event (3h timer / 1С posting). See spec §5 for the UI mapping.
 */
export interface EdoAuditEvent {
  id: string
  requestId: string
  /**
   * Код события. На бэке это СВОБОДНАЯ строка (`schema.prisma`: `event String`), поэтому union
   * здесь — единственное место, где список зафиксирован: он перечисляет всё, что реально пишут
   * `edo.service.ts` и `admin/edo-ops.service.ts`. До 13.08.2026 тут было 8 кодов из 20, и
   * недостающие рендерились в ленте сырой латиницей. Словари подписей в AuditTimeline.tsx
   * типизированы этим union'ом — забыть подпись для нового кода компилятор больше не даст.
   */
  event:
    | 'created' | 'route' | 'decision' | 'revised'
    | 'claimed' | 'unclaimed' | 'assigned' | 'reassigned'
    | 'escalation' | 'approved' | 'accrued'
    | 'posted' | 'post_failed' | 'post_unverified' | 'post_sync_stuck' | 'repost_requested'
    | 'registered' | 'closed' | 'rejected' | 'cancelled' | 'approver_added'
    | 'order_scans_updated'
    | 'termination_draft_withdrawn' | 'termination_draft_result' | 'termination_posting_confirmed'
    | 'termination_submitted' | 'termination_completed' | 'sla_reminder'
    | 'requisites_filled'
    // IT_WORKS: подача черновика, закрытие этапа модулей; no_executor — шаг без адресата (любой тип)
    | 'submitted' | 'it_modules_closed' | 'no_executor'
  fromStatus: EdoStatus | null
  toStatus: EdoStatus | null
  comment: string | null
  /**
   * «Было → стало» по изменённым полям payload — только у события `revised` (правка инициатора
   * на доработке, К1). Из этих событий строится версионность: v1 = подача, каждый revised = +1.
   */
  diff?: Record<string, { from: unknown; to: unknown }> | null
  createdAt: string
  actor: { id: string; fullName: string } | null
}

/**
 * GET /edo/tasks/:id/candidates — кандидаты для push-назначения (пикер). canAssign=false для
 * не-лидов (список пуст — роль наружу не раскрываем). candidates = члены группы минус инициатор.
 */
export interface TaskCandidates {
  canAssign: boolean
  currentAssigneeId: string | null
  candidates: { id: string; fullName: string }[]
}

/**
 * Вложение заявки ЭДО (GET /edo/requests/:id → attachments[]). Сам файл отдаётся защищённо —
 * GET /edo/attachments/:uploadId (Bearer + ACL по участникам заявки), НЕ через api.fileUrl().
 */
export interface EdoAttachment {
  id: string
  kind: string // 'resume' | 'order_scan' | 'certificate' | …
  uploadId: string
  createdAt: string
  upload: { name: string; mime: string; size: number }
}

/** GET /edo/requests/:id — full card: tasks + initiator + signer + audit timeline. */
/**
 * Шаг ПОЛНОГО маршрута согласования (GET /edo/requests/:id → route[]). В отличие от tasks[]
 * содержит и ещё не созданные шаги — маршрут виден целиком на любом статусе документа.
 * Названия шага/группы не локализованы сервером: фронт берёт STAGE_ACT[stage] / GROUP_LABEL[group].
 */
export interface EdoRouteStep {
  stage: EdoStage
  state: 'done' | 'current' | 'pending' | 'rejected' | 'skipped' | 'na'
  taskId?: string
  /** Ответственный будущего шага, пока персона не назначена («hr», «gd», …). */
  group?: string
  person?: { id: string; fullName: string; jobTitle: string | null }
  /** Кто может взять групповой шаг — только предпросмотр маршрута (GET /edo/route-preview). */
  candidates?: { id: string; fullName: string; jobTitle: string | null }[]
  decidedAt?: string
  comment?: string
}

export interface RequestCard extends EdoRequest {
  /** Версия документа: 1 + число правок на доработке (сервер считает по аудиту, revised). */
  version?: number
  tasks: EdoTask[]
  /** Полный маршрут согласования — источник правды для карточки И боковой панели. */
  route?: EdoRouteStep[]
  initiator: { id: string; fullName: string; externalId: string | null }
  signedBy: { id: string; fullName: string } | null
  /** Замещающий на время отпуска (заявка VACATION); null — не назначен. */
  deputy?: { id: string; fullName: string } | null
  auditEvents: EdoAuditEvent[]
  attachments?: EdoAttachment[]
  /** Смотрящий (бухгалтер/админ) может начислить отпускные по этой заявке (ТЗ-2026-004 шаг 11). */
  canAccrue?: boolean
  /** Смотрящий может отозвать заявку: инициатор / HR компании заявки / admin, пока она не в 1С. */
  canCancel?: boolean
  /**
   * Смотрящий может взять невзятую групповую задачу в работу: настоящий носитель роли (не заместитель
   * по отпуску), не инициатор. `false` — «Взять в работу» не показываем, решения остаются в футере.
   * Старый бэк поля не отдаёт — поведение как раньше.
   */
  canClaim?: boolean
  /** Смотрящий может поправить приказы закрытого шага приказа: HR компании заявки / admin. */
  canEditOrderScans?: boolean
  /**
   * Кого смотрящий замещает на время отпуска в юрлице заявки: личные задачи этих людей он решает
   * как свои (сервер пускает, assertCanAct). Старый бэк поля не отдаёт — задача остаётся «чужой».
   */
  actingFor?: string[]
  /** Увольнение: результаты Exit-интервью — только HR компании, CEO и admin (иначе поля нет). */
  exitInterview?: import('../features/edo/termination-types').ExitInterviewView | null
  /** SLA шага приказа (ТЗ-2026-002 §9): дедлайн 5 раб. дней + просрочка. null вне шага. */
  orderSla?: { dueAt: string; days: number; overdue: boolean } | null
  /**
   * «Информируемые» участники (ТЗ-2026-015 §5.1) — с ФИО и должностью. В `route[]` их НЕТ: там
   * только шаги маршрута, а информируемый шагом не является. Пусто у процессов без §5.1.
   */
  observers?: { id: string; fullName: string; jobTitle: string | null }[]
  /**
   * Куда можно добавить согласующего в идущий документ (сервер считает тем же insertionIndex, что
   * и POST /edo/requests/:id/approvers). null/нет поля — добавлять некуда: не FREEFORM/CONTRACT, не
   * на согласовании или договор уже у финального. beforeFinal — у маршрута есть фиксированный финал
   * договора: «в конец» встаёт прямо перед ним.
   */
  addApproverSlot?: { beforeFinal: boolean } | null
  /** IT_WORKS: раунд, состояние строк модулей, люди заявки, протокол и строки, которые я могу решить сейчас. */
  itWorks?: {
    round: number
    lines: ItWorksLineState[]
    contractor: { id: string; name: string; taxId: string | null } | null
    finalApprover: ItWorksPerson | null
    recipients: ItWorksPerson[]
    /** PDF-протокол принятия; null, пока работы не приняты. */
    protocolUploadId: string | null
    /** Строки, которые смотрящий (или он как заместитель) может решать СЕЙЧАС, в т.ч. уже решённые в открытом раунде. */
    canDecideLines: string[]
  }
}

// ─────────── Notifications ───────────
export interface Notification {
  id: string
  type: string
  title: string
  body?: string | null
  link?: string | null
  isRead: boolean
  createdAt: string
}

/** Push device registration (wave 2.4). Sending is phase 2; register tokens now. */
export type DevicePlatform = 'ios' | 'android' | 'web'
export interface DeviceRegistration {
  token: string
  platform: DevicePlatform
}

// ─────────── Preferences (wave 1.5) ───────────
export type PrefLanguage = 'ru' | 'en' | 'az' | 'ka'
export type PrefTheme = 'light' | 'dark' | 'system'
export type PrefAccent = 'navy' | 'slate' | 'teal' | 'forest' | 'wine'

export interface UserPreferences {
  language: PrefLanguage
  theme: PrefTheme
  accent: PrefAccent
  notifications: {
    mentions: boolean
    tasks: boolean
    news: boolean
    communities: boolean
    channel: { push: boolean; email: boolean }
  }
  privacy: { showStatus: boolean; showBirthday: boolean; showPhone: boolean }
}

/** PATCH /users/me/preferences — частичный; notifications/privacy мёржатся вглубь. */
export type UserPreferencesPatch = {
  language?: PrefLanguage
  theme?: PrefTheme
  accent?: PrefAccent
  notifications?: Partial<UserPreferences['notifications']> & {
    channel?: Partial<UserPreferences['notifications']['channel']>
  }
  privacy?: Partial<UserPreferences['privacy']>
}

// ─────────── Learning / LMS (wave 1.1) ───────────
export interface Course {
  id: string
  title: Localized
  cat: Localized
  lessons: number
  hours: number
  tone: Tone
  progress: number // 0..100 — для текущего юзера
  enrolled: boolean
}

export interface CourseDetail extends Course {
  description: Localized
  completedAt: string | null // ISO
}

export interface LearningSummary {
  inProgress: number
  completed: number
  totalLessons: number
}

// ─────────── Calendar (wave 1.2) ───────────
export interface CalEvent {
  id: string
  date: string // ISO
  endDate: string | null // ISO
  allDay: boolean
  tone: Tone
  title: Localized
  kind: Localized
  link: Localized | null
  source: string // напр. "HOLIDAY"
  region: string // напр. "AZ"
}

// Производственный календарь (P12): тип каждого дня — для окраски грида по данным,
// а не по дню недели. Праздники дублируются как CalEvent(source=HOLIDAY) с именами.
export interface ProductionDay {
  date: string // YYYY-MM-DD
  dayType: 'holiday' | 'workday' | 'saturday' | 'sunday' | 'pre_holiday'
  isWorkingDay: boolean
  workHours: number
  swapDate: string | null // YYYY-MM-DD, если день перенесён (в 2026 всегда null)
  calendar: string // код календаря, напр. "AR"
}

// ─────────── Communities (wave 1.3) ───────────
export interface Community {
  id: string
  name: Localized
  icon: string
  tone: Tone
  members: number
  joined: boolean
}

export interface CommunityDetail extends Community {
  description: Localized
}

// ─────────── Help / FAQ (wave 1.4) ───────────
export interface Faq {
  id: string
  q: Localized
  a: Localized
  cat: Localized
  tone: Tone
}

// ─────────── My tasks — личный todo (wave 1.7) ───────────
export type TaskPriority = 'high' | 'mid' | 'low'

export interface MyTask {
  id: string
  title: string
  due: string | null // ISO
  priority: TaskPriority
  done: boolean
  doneAt: string | null // ISO
}

export interface MyTaskInput {
  title: string
  due?: string | null
  priority?: TaskPriority
  done?: boolean
}

// ─────────── Global search (wave 3.3) ───────────
// Точные формы PersonCard/PostLite не зафиксированы в доке — свериться со Swagger.
export interface PersonCard {
  id: string
  fullName: string
  jobTitle?: string | null
  department?: string | null
  avatarUrl?: string | null
}

export interface PostLite {
  id: string
  title: string
  channel?: string
  createdAt?: string
}

export interface SearchResults {
  people: PersonCard[]
  posts: PostLite[]
}

// ─────────── News (🔶 wave «news-media») ───────────
export type NewsStatus = 'DRAFT' | 'PUBLISHED'

/** GET /news (list, без body) и /news/:id (деталь, с body). */
export interface NewsItem {
  id: string
  title: string
  excerpt: string | null
  body?: string // ТОЛЬКО в детали /news/:id; в списке отсутствует
  status: NewsStatus
  isPinned: boolean
  coverUrl: string | null // обложка → первая картинка → null
  attachments: Attachment[]
  author: Author
  publishedAt: string | null // ISO
  createdAt: string
  updatedAt: string
}

/** Курсорная страница GET /news → { items (без body), nextCursor }. */
export interface NewsPage {
  items: NewsItem[]
  nextCursor: string | null
}

/** Body для POST/PATCH /news (admin). attachmentIds — id загрузок с purpose=news. */
export interface NewsInput {
  title: string
  body: string
  excerpt?: string
  status?: NewsStatus
  isPinned?: boolean
  attachmentIds?: string[]
  coverId?: string
}

// ─────────── Calendar mail-import (Microsoft 365 / Graph, 🔶) ───────────
/** Статус подключения почтового календаря. null = ещё не подключали. */
export type GraphConnStatus = 'active' | 'error' | 'revoked' | null

/** GET /calendar/graph/status. */
export interface GraphStatus {
  configured: boolean
  connected: boolean
  status: GraphConnStatus
  lastSyncedAt: string | null
  lastError: string | null
}

/** GET /calendar/graph/connect → URL согласия Entra (фронт редиректит юзера). */
export interface GraphConnect {
  url: string
}

/** POST /calendar/graph/sync — ручная инкрементальная синхронизация. */
export interface GraphSyncResult {
  userId: string
  upserted: number
  removed: number
  status: GraphConnStatus
  error?: string
}

// ── KB / BookStack (PoC, вариант C) ──────────────────────────────────────────
export interface KbConfig {
  /** Базовый адрес BookStack — для deep-link'ов авторинга (гибрид). */
  baseUrl: string
}
export interface KbBook {
  id: number
  name: string
  slug: string
  description: string
  cover: string | null
  url: string
}
export interface KbPageRef {
  id: number
  name: string
  slug: string
  url: string
}
export interface KbSection {
  /** id главы; null — свободные страницы книги (вне глав). */
  id: number | null
  name: string | null
  pages: KbPageRef[]
}
export interface KbBookDetail {
  id: number
  name: string
  description: string
  sections: KbSection[]
  url: string
  createPageUrl: string
}
export interface KbPage {
  id: number
  name: string
  html: string
  bookId: number
  updatedAt: string
}
export interface KbSearchHit {
  id: number
  name: string
  type: string
  preview: string
  bookId: number | null
  url: string
}
// ── Подписание через SIMA İmza (web2app 2.0) ──────────────────────────────────
/** Отчёт sidecar'а pyHanko о подписях внутри PDF. */
/** Отчёт sidecar'а о подписях внутри PDF: цела ли каждая и что она покрывает. */
export interface PdfSignatureReport {
  ok: boolean
  count: number
  signatures: { field?: string; intact?: boolean; coverage?: string; signer?: string }[]
  detail?: string
}

export interface SigningOperation {
  id: string
  /** Auth — подтверждение входа, Sign — подпись документа. */
  type: 'Auth' | 'Sign'
  status: 'created' | 'data_served' | 'completed' | 'failed' | 'expired' | 'cancelled'
  assignee: string[]
  nbfUtc: number
  expUtc: number
  stepNo: number
  routeId: string | null
  documentName: string | null
  resultUploadId: string | null
  signerFin: string | null
  signerVoen: string | null
  signerSubject: string | null
  pdfCheck: PdfSignatureReport | null
  error: string | null
  createdAt: string
  completedAt: string | null
  /** Ссылка талона: из неё рисуется QR. Пересобирается сервером, хранить не нужно. */
  link: string | null
}

/**
 * Карточка публичной страницы `/s/<токен>`: по ней договор подписывает контрагент, у которого
 * учётной записи в портале нет. Поэтому здесь только то, что ему нужно видеть — ни инициатора,
 * ни номеров файлов в хранилище (документ отдаётся по той же ссылке, отдельной ручкой).
 */
export interface PublicSigningCard {
  documentName: string | null
  /** Отправитель — так же, как его видно в приложении SIMA. */
  sender: string
  status: SigningOperation['status']
  expUtc: number
  link: string | null
  pdfCheck: PdfSignatureReport | null
}
